import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { ProtectedRoute } from "@/components/shared/protected-route";
import { LoadingSpinner } from "@/components/shared/loading-spinner";

/* ─── Lazy-loaded pages ───────────────────────────────────────── */
const Login = lazy(() => import("@/pages/Login"));
const EntraCallback = lazy(() => import("@/pages/EntraCallback"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Organizations = lazy(() => import("@/pages/organizations"));
const PracticeSignups = lazy(() => import("@/pages/practice-signups"));
const CommunityAdmin = lazy(() => import("@/pages/community"));
const OrganizationNew = lazy(() => import("@/pages/organizations/new"));
const OrganizationPlans = lazy(() => import("@/pages/organizations/plans"));
const OrganizationDetail = lazy(() => import("@/pages/organizations/[id]"));
const OrganizationClearinghouse = lazy(() => import("@/pages/organizations/clearinghouse"));
const OrganizationG10Cert = lazy(() => import("@/pages/organizations/g10-cert"));
const OrganizationClaimRevLab = lazy(() => import("@/pages/organizations/claimrev-lab"));
const OrganizationWeno = lazy(() => import("@/pages/organizations/weno"));
const OrganizationMessaging = lazy(() => import("@/pages/organizations/messaging"));
const SMSIntegration = lazy(() => import("@/pages/integrations/sms"));
const WenoDrugDB = lazy(() => import("@/pages/weno-drug-db"));
const WenoPharmacyDirectory = lazy(() => import("@/pages/weno-pharmacy-directory"));
const WenoEprescribingGuide = lazy(() => import("@/pages/weno/eprescribing-guide"));
const DemoRequests = lazy(() => import("@/pages/demo-requests"));
const DemoRequestNew = lazy(() => import("@/pages/demo-requests/new"));
const DemoRequestDetail = lazy(() => import("@/pages/demo-requests/[id]"));
const Users = lazy(() => import("@/pages/users"));
const UserCreate = lazy(() => import("@/pages/users/create"));
const UserDetail = lazy(() => import("@/pages/users/[id]"));
const Roles = lazy(() => import("@/pages/roles"));
const Facilities = lazy(() => import("@/pages/facilities"));
const FacilityDetail = lazy(() => import("@/pages/facilities/[id]"));
const AuditLogs = lazy(() => import("@/pages/audit-logs"));
const AuditLogDetail = lazy(() => import("@/pages/audit-logs/[id]"));
const SystemHealth = lazy(() => import("@/pages/system-health"));
const JobDetail = lazy(() => import("@/pages/system-health/job"));
const Integrations = lazy(() => import("@/pages/integrations"));
const CodeSystems = lazy(() => import("@/pages/code-systems"));
const BillingConfig = lazy(() => import("@/pages/billing-config"));
const BillingOverviewPage = lazy(() => import("@/pages/billing/overview"));
const BillingAccountsPage = lazy(() => import("@/pages/billing/accounts"));
const BillingAccountPage = lazy(() => import("@/pages/billing/account"));
const BillingInvoicesPage = lazy(() => import("@/pages/billing/invoices"));
const BillingInvoicePrintPage = lazy(() => import("@/pages/billing/invoice-print"));
const PublicQuotePage = lazy(() => import("@/pages/public-quote"));
const BillingInvoicePage = lazy(() => import("@/pages/billing/invoice"));
const BillingPaymentsPage = lazy(() => import("@/pages/billing/payments"));
const BillingSettingsPage = lazy(() => import("@/pages/billing/settings"));
const BillingGuide = lazy(() => import("@/pages/billing-guide"));
const Reports = lazy(() => import("@/pages/reports"));
const Webhooks = lazy(() => import("@/pages/webhooks"));
const Settings = lazy(() => import("@/pages/settings"));
const AIGuardrails = lazy(() => import("@/pages/ai-guardrails"));
const OnboardingPortal = lazy(() => import("@/pages/portal/onboarding/[token]"));
const GetPricing = lazy(() => import("@/pages/get-pricing"));

function PageFallback() {
  return (
    <div className="flex h-[60vh] items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

/* ─── App ─────────────────────────────────────────────────────── */

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<EntraCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/portal/onboarding/:token" element={<OnboardingPortal />} />
        <Route path="/get-pricing" element={<GetPricing />} />
        <Route path="/q/:token" element={<PublicQuotePage />} />

        {/* Protected routes — requires authentication + admin role */}
        <Route element={<ProtectedRoute />}>
          {/* Full-page document views (no admin chrome, printable) */}
          <Route path="/billing/invoices/:invoiceId/print" element={<BillingInvoicePrintPage />} />
          <Route element={<AdminLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/organizations/new" element={<OrganizationNew />} />
            <Route path="/organizations/plans" element={<OrganizationPlans />} />
            <Route path="/organizations/:id" element={<OrganizationDetail />} />
            <Route path="/organizations/:id/clearinghouse" element={<OrganizationClearinghouse />} />
            <Route path="/organizations/:id/g10-cert" element={<OrganizationG10Cert />} />
            <Route path="/organizations/:id/claimrev-lab" element={<OrganizationClaimRevLab />} />
            <Route path="/organizations/:id/weno" element={<OrganizationWeno />} />
            <Route path="/organizations/:id/messaging" element={<OrganizationMessaging />} />
            <Route path="/weno/drug-db" element={<WenoDrugDB />} />
            <Route path="/weno/pharmacy-directory" element={<WenoPharmacyDirectory />} />
            <Route path="/weno/guide" element={<WenoEprescribingGuide />} />
            <Route path="/practice-signups" element={<PracticeSignups />} />
            <Route path="/community" element={<CommunityAdmin />} />
            <Route path="/demo-requests" element={<DemoRequests />} />
            <Route path="/demo-requests/new" element={<DemoRequestNew />} />
            <Route path="/demo-requests/:id" element={<DemoRequestDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/users/create" element={<UserCreate />} />
            <Route path="/users/:id" element={<UserDetail />} />
            <Route path="/roles" element={<Roles />} />
            <Route path="/facilities" element={<Facilities />} />
            <Route path="/facilities/:id" element={<FacilityDetail />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
            <Route path="/audit-logs/:id" element={<AuditLogDetail />} />
            <Route path="/system-health" element={<SystemHealth />} />
            <Route path="/system-health/jobs/:name" element={<JobDetail />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/integrations/sms" element={<SMSIntegration />} />
            <Route path="/code-systems" element={<CodeSystems />} />
            <Route path="/billing-config" element={<BillingConfig />} />
            <Route path="/billing" element={<BillingOverviewPage />} />
            <Route path="/billing/accounts" element={<BillingAccountsPage />} />
            <Route path="/billing/accounts/:orgId" element={<BillingAccountPage />} />
            <Route path="/billing/invoices" element={<BillingInvoicesPage />} />
            <Route path="/billing/invoices/:invoiceId" element={<BillingInvoicePage />} />
            <Route path="/billing/payments" element={<BillingPaymentsPage />} />
            <Route path="/billing/settings" element={<BillingSettingsPage />} />
            <Route path="/billing/receivables" element={<Navigate to="/billing/invoices" replace />} />
            <Route path="/billing-guide" element={<BillingGuide />} />
            <Route path="/ai-guardrails" element={<AIGuardrails />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/webhooks" element={<Webhooks />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
        </Route>


        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
